import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TCProductSelectStore, {
    AccessoryType,
    DeviceType,
    IndoorDevice,
    PriceResponse,
    ProductSelectActions,
    Grid,
} from '@/components/productselect/tc.product.select.store';
import { useStore, waitForStore } from '@tk/utilities/tk.use.store';
import TCDeviceQuantityCounter from './tc.device.quantity.counter';
import TCProductSelect from './tc.product.select';
import { fetchRequest } from '@tk/utilities/tk.fetch';

export default class TCGridCollection extends TKCustomElementFactory {
    store?: TCProductSelectStore;
    protected unsubscribe?: () => void;
    contextDeviceId: string | null;

    gridAttribute = 'data-tc-device="grid"';
    articleNumberAttribute = 'data-tc-artno';
    imgAttribute = 'data-tc-device-img';
    titleAttribute = 'data-tc-device-title';
    subTitleAttribute = 'data-tc-device-subtitle';
    quantityAttribute = 'data-tc-device-quantity';
    priceAttribute = 'data-tc-device-price';
    uvpAttribute = 'data-tc-device-price-uvp';
    deviceTypeAttribute = 'data-tc-device-type';
    inclusiveAttribute = 'data-tc-device-inclusive';
    activeAttribute = 'data-tc-device-active';
    activateButtonAttribute = 'data-tc-active-button';

    gridTemplateSelector = '[data-tc-grid-template]';
    gridWrapperSelector = '[data-tc-grid-wrapper]';
    gridWrapper?: HTMLElement;
    gridTemplate?: HTMLTemplateElement;

    productSelectTagName = 'tc-product-select';
    productSelect?: TCProductSelect;
    waitForPrices: boolean = false;

    constructor() {
        super();
        this.contextDeviceId = this.getAttribute('data-tc-context-device');
        this.gridTemplate = document.querySelector(this.gridTemplateSelector) || undefined;
        this.gridWrapper = this.querySelector(this.gridWrapperSelector) || undefined;
        this.productSelect = document.querySelector(this.productSelectTagName) as TCProductSelect;
    }

    async connectedCallback() {
        if (!this.contextDeviceId) throw new Error('Missing context device id!');
        await waitForStore('productSelectStore');
        this.store = useStore('productSelectStore') as TCProductSelectStore;
        this.initializeStore();
    }

    disconnectedCallback(): void {
        this.unsubscribe && this.unsubscribe();
    }

    initializeStore() {
        if (!this.store) return;
        this.unsubscribe = this.store.subscribe(({ action }) => this.render(action as ProductSelectActions));
        this.sendInitialDataToStore();
        this.productSelect?.asyncPrices && this.store?.getLoginStatus() && this.fetchPrices();
        this.registerClickListener();
    }

    sendInitialDataToStore() {
        const contextDevice: IndoorDevice | undefined = this.store?.getDevice(this.contextDeviceId!) as IndoorDevice;
        if (contextDevice.deviceType !== DeviceType.INDOOR) return;
        if (contextDevice.gridsInitalized === true) {
            this.generateGrids();
            return;
        }

        const gridElements = this.querySelectorAll(`[${this.gridAttribute}]`);
        const devices: Grid[] = [];
        if (gridElements.length === 0) return;

        gridElements.forEach((element) => {
            const gridDevice: Grid = {
                id: element.id,
                artNo: element.getAttribute(this.articleNumberAttribute) || '',
                title: element.getAttribute(this.titleAttribute) || '',
                subtitle: element.getAttribute(this.subTitleAttribute) || undefined,
                price: element.getAttribute(this.priceAttribute) || '',
                uvpPrice: element.getAttribute(this.uvpAttribute) || '',
                image: element.getAttribute(this.imgAttribute) || undefined,
                isActive: element.getAttribute(this.activeAttribute) === '1',
                inclusive: element?.getAttribute(this.inclusiveAttribute) === '1',
                parentId: this.contextDeviceId!,
                deviceType: DeviceType.GRID,
                hasSiblings: false,
            };

            devices.push(gridDevice);
        });

        this.store?.setGrids(this.contextDeviceId!, devices);
    }

    registerClickListener() {
        this.pushListener({
            event: 'click',
            element: this,
            action: this.handleClick.bind(this),
        });
    }

    handleClick(event: Event) {
        const target = event.target as HTMLElement;

        if (target.closest(`[${this.activateButtonAttribute}]`)) {
            const deviceElement = target.closest(`[${this.gridAttribute}]`);
            const id = deviceElement?.id;
            if (!this.contextDeviceId || !id) return;
            this.store?.setActiveGrid(this.contextDeviceId, id);
        }
    }

    render(action: ProductSelectActions) {
        if (this.waitForPrices && action === ProductSelectActions.SET_ACCESSORY_PRICES) this.renderPrices();
        if (action !== ProductSelectActions.SET_ACTIVE_GRID) return;
        this.generateGrids();
    }

    generateGrids() {
        const grids = this.store?.getGrids(this.contextDeviceId!);
        if (grids?.length === 0 || !this.gridTemplate || !this.gridWrapper) return;
        this.gridWrapper.innerHTML = '';
        grids!.forEach((grid) =>
            TCGridCollection.renderGrid(grid, this.gridTemplate!, this.gridWrapper!, 1, !!this.store?.getLoginStatus()),
        );
    }

    static renderGrid(
        grid: Grid,
        template: HTMLTemplateElement,
        wrapper: HTMLElement,
        quantity = 1,
        loginStatus: boolean,
    ) {
        const fragment = template.content.cloneNode(true) as DocumentFragment;
        const gridElement = fragment.firstElementChild as HTMLElement;
        if (!gridElement) return;

        gridElement.id = grid.id;

        const img = gridElement.querySelector('[data-tc-device-img]') as HTMLImageElement;
        if (img && grid.image) {
            img.src = grid.image;
        }

        const title = gridElement.querySelector('[data-tc-device-title]');
        if (title) title.textContent = grid.title;

        const subtitle = gridElement.querySelector('[data-tc-device-subtitle]');
        if (subtitle) subtitle.innerHTML = grid.subtitle || '';

        if (grid.isActive) gridElement.classList.add('remote--active');
        if (grid.inclusive) gridElement.classList.add('remote--inclusive');
        if (grid.parentId) {
            const changeButton = gridElement.querySelector('[data-tc-change-grid-button]') as HTMLElement;
            if (changeButton) {
                changeButton.dataset.tkUrl += grid.parentId;
            }
        }

        if (!loginStatus) wrapper.appendChild(gridElement);
        else {
            const price = gridElement.querySelector('[data-tc-device-price]');
            const priceContent = TCDeviceQuantityCounter.calculateTotalPrice(grid.price, quantity);
            if (priceContent !== '-') {
                price && (price.textContent = priceContent);
            } else {
                const priceWrapper = price?.closest('[data-tc-device-price-wrapper]') as HTMLElement;
                priceWrapper && priceWrapper.setAttribute('hidden', 'true');
            }

            wrapper.appendChild(gridElement);
        }
    }

    fetchPrices() {
        this.waitForPrices = true;
        const ids = this.store?.getGridIds(this.contextDeviceId!);
        if (!ids || !this.productSelect?.priceUrl) return;
        const payload = {
            ids,
        };

        fetchRequest({
            requestURL: this.productSelect.priceUrl,
            resolveHandler: this.updatePrices.bind(this),
            payload,
        });
    }

    updatePrices(response: TKResponse<PriceResponse>) {
        if (!response.success || !response.dataAsJson) return;
        this.store?.setAccessoryPrices(AccessoryType.GRID, this.contextDeviceId!, response.dataAsJson);
    }

    renderPrices() {
        this.waitForPrices = false;
        this.generateGrids();
    }
}
